import { fabric } from 'fabric'
import { v4 as guid } from 'uuid'
import { pick } from 'lodash-es'
import RenderHelper from './renderHelper'
import { whiteboardConstants } from '@/models/constants'

interface IWbStickyOptions extends fabric.IRectOptions, IWbObjectProps {
  id?: string
  font?: string
  fontSize?: number
  textAlign?: CanvasTextAlign
  textColor?: string
  bold?: boolean
  italic?: boolean
  lock?: boolean
  preventUnlock?: boolean
  preventDelete?: boolean
}

export default class WbSticky extends fabric.Rect implements IWbObject {
  public id: string
  public type = whiteboardConstants.objectTypes.sticky
  public text = ''
  public textAlign: CanvasTextAlign = 'center'
  public font = 'Helvetica'
  public fontSize = -1 // Auto
  public textColor = '#000000'
  public bold = false
  public italic = false
  public lock: boolean
  public preventUnlock: boolean
  public connectable: boolean
  public preventDelete?: boolean
  public localOnly?: boolean
  public excludeFromGroupSelection?: boolean
  public canLink = true
  public canBeLinkedTo = false
  public link?: { id: string, x: number, y: number }
  public editableProps: Record<string, IWbObjectProp> = {
    color: { name: 'color', type: 'color', editable: true },
    font: { name: 'font', type: 'font', editable: true },
    fontSize: { name: 'font size', type: 'fontSize', editable: true },
    textColor: { name: 'text color', type: 'textColor', editable: true },
    // 'textAlign': { name: 'align', type: 'textAlign' },
    fontStyle: { name: 'font style', type: 'fontStyle', editable: true },
    size: { name: 'size', type: 'size', editable: true },
    lock: { name: 'Lock', type: 'lock', editable: true, editableWhenLocked: true },
    addDiscussion: { name: 'add comment', type: 'addDiscussion', editable: true, editableWhenLocked: true },
  }

  public actions: Record<string, IWObjectActions> = {
    unlink: { action: 'unlink', label: 'Unlink', faicon: 'fa-light fa-link-simple-slash', showInSubMenu: true },
    selectSimilar: { action: 'selectSimilar', label: 'Select Similar', faicon: 'fa-light fa-check-double', showInSubMenu: true },
    bringFront: { action: 'bringFront', label: 'Bring to Front', faicon: 'fa-light fa-bring-front', showInSubMenu: true },
    sendBack: { action: 'sendBack', label: 'Send to Back', faicon: 'fa-light fa-send-back', showInSubMenu: true },
    // group: { action: 'group', label: 'Group', faicon: 'fa-light fa-object-group', showInSubMenu: true, multiple: true },
    cloneAsImage: { action: 'cloneAsImage', label: 'Clone As Image', faicon: 'fa-light fa-clone', showInSubMenu: true },
    delete: { action: 'delete', label: 'Remove', faicon: 'fa-light fa-trash-can', showInSubMenu: true },
  }

  private cachedFontProps = {
    w: 0,
    h: 0,
    text: '',
    font: '',
    fontSize: -1,
    textAlign: 'center',
    bold: false,
    italic: false,
  }

  constructor(text: string, opt?: IWbStickyOptions) {
    super(opt)
    this.id = opt?.id || guid()
    this.text = text
    this.textAlign = opt?.textAlign || 'center'
    this.font = opt?.font || 'Helvetica'
    this.fontSize = opt?.fontSize || -1
    this.textColor = opt?.textColor || '#000000'
    this.bold = opt?.bold || false
    this.italic = opt?.italic || false
    this.lock = opt?.lock || false
    this.shadow = new fabric.Shadow('rgba(0,0,0,0.2) 2px 2px 10px')
    this.connectable = true
    this.preventUnlock = opt?.preventUnlock || false
    this.originX = 'left'
    this.originY = 'top'
    this.preventDelete = opt?.preventDelete || false
    this.localOnly = opt?.localOnly || false
    this.excludeFromGroupSelection = opt?.excludeFromGroupSelection || false
    this.objectCaching = false

    this.setLock(this.lock)

    this.stateProperties = this.stateProperties?.concat(['font', 'fontSize', 'textAlign', 'textColor', 'bold', 'italic', 'text', 'lock', 'preventUnlock'])

    this.on('scaling', () => {
      this.width! *= this.scaleX!
      this.height! *= this.scaleY!
      this.scaleX = 1
      this.scaleY = 1
      this.dirty = true
      this.setCoords()
      this.canvas?.requestRenderAll()
    })
  }

  setProp(prop: string, value: any) {
    switch (prop) {
      case 'color':
        this.set('fill', value.backgroundColor)
        break
      case 'font':
        this.set('font', value.font)
        break
      case 'fontSize':
        this.set('fontSize', value.fontSize)
        break
      case 'textColor':
        this.set('textColor', value.textColor)
        break
      case 'textAlign':
        this.set('textAlign', value.textAlign)
        break
      case 'bold':
        this.set('bold', value.bold)
        break
      case 'italic':
        this.set('italic', value.italic)
        break
      case 'size': {
        let size = 100
        switch (value.size) {
          case 'S':
            size = 50
            break
          case 'M':
            size = 100
            break
          case 'L':
            size = 200
            break
        }
        this.set('width', size)
        this.set('height', size)
        break
      }
      case 'text':
        this.set('text', value.text)
        break
      case 'lock':
        this.set('lock', value.lock)
        this.setLock(value.lock)
        break
      default:
        console.warn('Attempting to set unsupported WbObjectProp', prop, value)
        return
    }
    this.dirty = true
    this.canvas?.requestRenderAll()
    this.canvas?.fire('object:modified', { target: this })
  }

  getProp(prop: string) {
    const result: any = {}
    switch (prop) {
      case 'color':
        result.backgroundColor = this.fill
        break
      case 'font':
        result.font = this.font
        break
      case 'fontSize':
        result.fontSize = this.fontSize
        break
      case 'textColor':
        result.textColor = this.textColor
        break
      case 'textAlign':
        result.textAlign = this.textAlign
        break
      case 'bold':
        result.bold = this.bold
        break
      case 'italic':
        result.italic = this.italic
        break
      case 'size':
        result.size = this.width === 50
          ? 'S'
          : this.width === 100
            ? 'M'
            : this.width === 200
              ? 'L'
              : 'C'
        break
      case 'text':
        result.text = this.text
        break
      case 'lock':
        result.lock = this.lock
        break
      default:
        console.warn('Attempting to get unsupported WbObjectProp', prop)
    }
    return result
  }

  setLock(lock: boolean) {
    this.set('lockMovementX', lock)
    this.set('lockMovementY', lock)
    this.set('lockRotation', lock)
    this.set('lockScalingFlip', lock)
    this.set('lockScalingX', lock)
    this.set('lockScalingY', lock)
    this.set('hasControls', !lock)
  }

  override toObject() {
    const propsToPluck = [
      'id',
      'type',
      'top',
      'left',
      'angle',
      'width',
      'height',
      'scaleX',
      'scaleY',
      'fill',
      'backgroundColor',
      'shadow',
      'textColor',
      'bold',
      'italic',
      'text',
      'font',
      'fontSize',
      'textAlign',
      'lock',
      'preventUnlock',
      'version',
      'localOnly',
      'link',
    ]
    return pick(this, propsToPluck)
  }

  static fromObject(object: fabric.Object, callback?: Function) {
    return fabric.Object._fromObject(whiteboardConstants.objectTypes.sticky, object, callback, 'text')
  }

  override _render(ctx: CanvasRenderingContext2D): void {
    const h = this.height || 0
    const w = this.width || 0
    const xStart = -w / 2
    const yStart = -h / 2
    // const xEnd = xStart + w
    // const yEnd = yStart + h
    // const foldPercent = 0.3
    const xMargin = w * 0.025 // 10
    const yMargin = h * 0.05 // 10

    // Draw rectangle
    super._render(ctx)

    // Draw paper fold
    // ctx.beginPath()
    // ctx.moveTo(xEnd, yEnd - yEnd * foldPercent)
    // ctx.lineTo(xEnd - xEnd * foldPercent, yEnd - yEnd * foldPercent)
    // ctx.lineTo(xEnd - xEnd * foldPercent, yEnd)
    // ctx.closePath()
    // ctx.fillStyle = 'rgba(0,0,0,0.1)'
    // ctx.fill()

    // Draw text
    // If font is auto, then loop until you find the size that fits
    ctx.fillStyle = this.textColor
    const fontStyle = `${(this.italic ? 'italic' : '')} ${(this.bold ? 'bold' : '')}`
    if (this.fontSize < 0) {
      if (this.cachedFontProps.w === w && this.cachedFontProps.h === h && this.cachedFontProps.text === this.text && this.cachedFontProps.font === this.font && this.cachedFontProps.fontSize > 0) {
        RenderHelper.drawTextInBox(ctx, this.font, this.cachedFontProps.fontSize, fontStyle, this.text, xStart + xMargin, yStart, w - xMargin * 2, h - yMargin * 2, 'center', 'middle', false, false)
      }
      else {
        console.log('Calculating font size for sticky')
        let fontSize = 500
        while (!RenderHelper.drawTextInBox(ctx, this.font, fontSize, fontStyle, this.text, xStart + xMargin, yStart, w - xMargin * 2, h - yMargin * 2, 'center', 'middle', true, false)) {
          fontSize--
          if (fontSize <= 0) { break }
        }
        this.cachedFontProps = {
          w,
          h,
          text: this.text,
          font: this.font,
          fontSize,
          textAlign: this.textAlign,
          bold: this.bold,
          italic: this.italic,
        }
      }
    }
    else {
      RenderHelper.drawTextInBox(ctx, this.font, this.fontSize, fontStyle, this.text, xStart + xMargin, yStart, w - xMargin * 2, h - yMargin * 2, 'center', 'middle', false, false)
    }
  }
}

const f: any = fabric
f.WbSticky = WbSticky
